import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { BookOpen } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { FormField } from '../components/FormField.tsx';
import { PortalShell } from '../components/PortalShell.tsx';
import { Select } from '../components/Select.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface Article {
  id: string;
  categoryId: string | null;
  categoryName: string | null;
  slug: string;
  title: string;
  audience: 'public' | 'internal';
  isPublished: boolean;
  authorAgentName: string | null;
  updatedAt: string;
}

interface Props {
  scope: 'agent' | 'portal';
}

export function KbListPage({ scope }: Props): React.ReactElement {
  useDocumentTitle('Knowledgebase');
  const { agent } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const categoryId = searchParams.get('categoryId') ?? '';
  const audience = searchParams.get('audience') ?? '';
  const q = searchParams.get('q') ?? '';

  const apiBase = scope === 'agent' ? '/api/kb' : '/api/portal/kb';

  const apiQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (categoryId) p.set('categoryId', categoryId);
    if (scope === 'agent' && audience) p.set('audience', audience);
    if (q) p.set('q', q);
    return p.toString();
  }, [categoryId, audience, q, scope]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ categories: Category[] }>(`${apiBase}/categories`)
      .then((r) => {
        if (!cancelled) setCategories(r.categories);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  async function reload() {
    const res = await apiFetch<{ articles: Article[] }>(
      `${apiBase}/articles${apiQuery ? `?${apiQuery}` : ''}`,
    );
    setArticles(res.articles);
  }

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ articles: Article[] }>(`${apiBase}/articles${apiQuery ? `?${apiQuery}` : ''}`)
      .then((r) => {
        if (!cancelled) {
          setArticles(r.articles);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load articles.');
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, apiQuery]);

  // Selection clears whenever the visible list changes (filter, search,
  // category) so we never silently retain off-screen ids.
  useEffect(() => {
    setSelected(new Set());
  }, [apiQuery]);

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  function toggleOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll(visible: Article[]) {
    if (visible.length > 0 && visible.every((a) => selected.has(a.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((a) => a.id)));
    }
  }

  async function bulkSetCategory(nextCategoryId: string | null) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ updated: number }>('/api/kb/articles/bulk-category', {
        method: 'POST',
        json: { ids: [...selected], categoryId: nextCategoryId },
      });
      toast.success(`Re-categorized ${r.updated} article${r.updated === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk category change failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} article${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) {
      return;
    }
    setBulkBusy(true);
    try {
      const r = await apiFetch<{ deleted: number }>('/api/kb/articles/bulk-delete', {
        method: 'POST',
        json: { ids: [...selected] },
      });
      toast.success(`Deleted ${r.deleted} article${r.deleted === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  const articleHref = (slug: string) =>
    scope === 'agent' ? `/kb/${slug}` : `/portal/kb/${slug}`;

  const body = (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Knowledgebase</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            {scope === 'agent'
              ? 'Internal runbooks and client-facing articles. Use the audience filter to switch.'
              : 'Self-service articles and guides.'}
          </p>
        </div>
        {scope === 'agent' && agent && (
          <div className="flex flex-wrap gap-2">
            {agent.role === 'admin' && (
              <>
                <Link to="/admin/kb-categories">
                  <Button variant="outline">Manage categories</Button>
                </Link>
                <Link to="/kb/import-zendesk">
                  <Button variant="outline">Import from Zendesk</Button>
                </Link>
              </>
            )}
            <Link to="/kb/new">
              <Button>New article</Button>
            </Link>
          </div>
        )}
      </div>

      <Card>
        <div className="grid gap-3 sm:grid-cols-3">
          <FormField
            label="Search"
            name="q"
            placeholder="Search title or body…"
            value={q}
            onChange={(e) => updateFilter('q', e.target.value)}
          />
          <Select label="Category" value={categoryId} onChange={(e) => updateFilter('categoryId', e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          {scope === 'agent' && (
            <Select label="Audience" value={audience} onChange={(e) => updateFilter('audience', e.target.value)}>
              <option value="">All</option>
              <option value="public">Public (clients can read)</option>
              <option value="internal">Internal only</option>
            </Select>
          )}
        </div>
      </Card>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {articles === null ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : articles.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-8 w-8" />}
          title="No articles match"
          body={
            scope === 'agent'
              ? "Try clearing filters above. If the KB is empty altogether, an admin can import from Zendesk or write a new article."
              : "We're still publishing articles here. Check back soon, or open a ticket if you need help."
          }
          action={
            scope === 'agent' && agent ? (
              <Link to="/kb/new">
                <Button>Write the first one</Button>
              </Link>
            ) : null
          }
        />
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          {scope === 'agent' && selected.size > 0 && articles.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-t3-blue)]/30 bg-[var(--color-t3-blue)]/5 px-4 py-2 text-sm">
              <span className="font-semibold text-[var(--color-header)]">
                {selected.size} selected
              </span>
              <select
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  void bulkSetCategory(e.target.value === '__none' ? null : e.target.value);
                }}
                disabled={bulkBusy}
                className="h-9 min-w-[200px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
              >
                <option value="">Set category…</option>
                <option value="__none">— None (uncategorized) —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {agent?.role === 'admin' && (
                <Button
                  variant="danger"
                  onClick={() => void bulkDelete()}
                  disabled={bulkBusy}
                  className="h-9 px-3 py-1.5"
                >
                  Delete selected
                </Button>
              )}
              {bulkBusy && (
                <span className="text-xs text-[var(--color-muted)]">Working…</span>
              )}
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                disabled={bulkBusy}
                className="ml-auto text-xs text-[var(--color-muted)] hover:underline disabled:opacity-60"
              >
                Clear
              </button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                {scope === 'agent' && (
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={articles.length > 0 && articles.every((a) => selected.has(a.id))}
                      onChange={() => toggleAll(articles)}
                      className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                    />
                  </th>
                )}
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                {scope === 'agent' && <th className="px-4 py-3">Audience</th>}
                {scope === 'agent' && <th className="px-4 py-3">Status</th>}
                <th className="px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr
                  key={a.id}
                  className={
                    'border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]/40 ' +
                    (scope === 'agent' && selected.has(a.id) ? 'bg-[var(--color-t3-blue)]/5' : '')
                  }
                >
                  {scope === 'agent' && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${a.title}`}
                        checked={selected.has(a.id)}
                        onChange={() => toggleOne(a.id)}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Link to={articleHref(a.slug)} className="font-medium text-[var(--color-t3-blue)] hover:underline">
                      {a.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{a.categoryName ?? '—'}</td>
                  {scope === 'agent' && (
                    <td className="px-4 py-3 text-xs uppercase tracking-wide">{a.audience}</td>
                  )}
                  {scope === 'agent' && (
                    <td className="px-4 py-3 text-xs">
                      {a.isPublished ? (
                        <span className="text-green-700">published</span>
                      ) : (
                        <span className="text-amber-700">draft</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {new Date(a.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (scope === 'portal') {
    return <PortalShell active="kb">{body}</PortalShell>;
  }
  return body;
}
